import { useQuery } from "@tanstack/react-query";

import { checkAddressDeliverability } from "Utilities/Address/AddressValidation/deliverability";

// Shared react-query namespace so the same address dedupes/caches across the
// commit check and any later re-check within a session.
const QUERY_KEY = "addressDeliverability";

// Case/space-insensitive key so "800 Main St" and "800 main st " are one cache
// entry — the backend normalizes anyway, this just avoids duplicate calls.
const addressKey = (address) =>
  ["addr1", "city", "state", "zip", "country"]
    .map((field) => (address?.[field] || "").trim().toLowerCase())
    .join("|");

/**
 * Resolve the backend USPS-deliverability verdict for a committed-verified
 * address, so the caller can gate the "USPS Verified" badge on real
 * deliverability instead of a Smarty autocomplete leaf match.
 *
 * Imports `checkAddressDeliverability` directly (the single, network-scoped
 * probe) — the same way `useSmartyCandidates` imports the autocomplete fetch;
 * tests drive it through the mocked fetch boundary (MockFnrFetch), not a stub.
 *
 * Returns `deliverable`:
 *   - `true`  — backend DPV confirms it (show the badge)
 *   - `false` — backend DPV rejects it (hide the badge)
 *   - `null`  — the probe failed (outage): unknown, not confirmed. The caller
 *               fails CLOSED and hides the badge — an optimistic surface must
 *               not assert a verification it couldn't confirm.
 *   - `undefined` — enabled but still in flight, or not enabled (also hidden)
 *
 * The query only runs when `enabled` — the caller passes true only for an
 * in-session leaf verification on an opt-in surface, never for a
 * backend-verified address opened for edit.
 *
 * @param {{ address: object, enabled: boolean }} params
 * @returns {{ deliverable: boolean|null|undefined }}
 */
export default function useDeliverabilityVerdict({ address, enabled }) {
  const active = Boolean(enabled && address?.addr1);

  const { data } = useQuery({
    queryKey: [QUERY_KEY, addressKey(address)],
    queryFn: () => checkAddressDeliverability(address),
    enabled: active,
    // A given address's deliverability doesn't change within a session, and a
    // failed probe should not auto-retry (the next edit re-queries a new key).
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return { deliverable: active ? data : undefined };
}

export { addressKey };
